import { Card } from "../../../types/ContentTypes";
import getImageUrl from "../../../helpers/getImageUrl.helper";
import { HMF_Image } from "../../Atoms/Image/image.component";
import { cn } from "@shared/helpers/classNames.helper";
import { Link } from "react-router-dom";
import { ReactNode } from "react";
import S from "./Cards.module.scss";
import { isLocalhost } from "@shared/config/config";

interface CardsProps {
    headline?: string;
    cards: Card[];
    settings?: {
        contentType: string;
        id: string;
        properties: {
            numberOfColumns?: number;
        };
    };
}

interface CardContentProps {
    title: string;
    description: { markup: string } | null;
}

const CardContent = ({ title, description }: CardContentProps) => (
    <>
        <h3 className={S.cardTitle}>{title}</h3>
        {description && (
            <p
                className={S.cardDescription}
                dangerouslySetInnerHTML={{ __html: description.markup }}
            />
        )}
    </>
);

interface CardLinkProps extends React.PropsWithChildren {
    path: string;
    target?: string;
    linkType?: string;
    children?: ReactNode[];
}

const CardLink = ({ path, target, linkType, children }: CardLinkProps) => {
    return linkType === "External" ? (
        <a className={S.link} href={path} target={target}>
            {children}
        </a>
    ) : (
        <Link className={S.link} to={path} target={target}>
            {children}
        </Link>        
    )
};

const Cards = ({ headline, cards, settings }: CardsProps) => {
    const numberOfColumns = settings?.properties?.numberOfColumns || (cards?.length < 6 ? cards?.length : 5);

    return (
        <div className={S.cardsContainer}>
            {headline && <h2 className={S.headline}>{headline}</h2>}
            <div
                className={cn(S.cardsGrid, numberOfColumns === 0 && S.cardsAutoFlow)}
                style={numberOfColumns > 0 ?
                    {
                        gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`,
                    } as React.CSSProperties : {}
                }
            >
                {cards.map((card) => {
                    const { id, properties } = card.content;
                    const { title, description, image, links } = properties;
                    const firstLink = links?.[0];

                    if (!firstLink) {
                        return (
                            <div key={id} className={S.card}>
                                <div>
                                    {image && image.length > 0 && (
                                        <div className={S.cardImageWrapper}>
                                            <HMF_Image
                                                src={getImageUrl(image[0].url)}
                                                alt={title}
                                                focalPoint={image[0].focalPoint}
                                                className={S.cardImage}
                                                onClick={undefined}
                                            />
                                        </div>
                                    )}
                                    <div className={S.cardContent}>
                                        <CardContent title={title} description={description} />
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    let path = firstLink.route?.path ?? "";
                    const target = firstLink.target ?? undefined;
                    const linkType = firstLink.linkType;

                    // @ts-expect-error
                    const urlPrefix = isLocalhost ? globalThis?.contentApiUrl : "";

                    if (linkType === "External") {
                        path =
                            firstLink.url?.startsWith("http") || firstLink.url?.startsWith("https")
                                ? firstLink.url
                                : `http://${firstLink.url}`;
                    } else if (linkType === "Media" && firstLink.url) {
                        path = `${urlPrefix}${firstLink.url}`;
                    } else {
                        path = firstLink.route?.path ?? "";
                    }

                    return (
                        <div key={id} className={cn(S.card, S.cardLink)}>
                            <CardLink linkType={linkType} path={path} target={target}>
                                {image && image.length > 0 && (
                                    <div className={S.cardImageWrapper}>
                                        <HMF_Image
                                            src={getImageUrl(image[0].url)}
                                            alt={title}
                                            focalPoint={image[0].focalPoint}
                                            className={S.cardImage}
                                        />
                                    </div>
                                )}
                                <div className={S.cardContent}>
                                    <CardContent
                                        title={title}
                                        description={description}
                                    />
                                </div>
                            </CardLink>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Cards;
